import React, { useState, useEffect } from 'react';
import { FolderOpen, Trash2, RefreshCw, Plus, Check } from 'lucide-react';

const VariantesPanel = ({ user, toolId, wizardData, setWizardData, showNotification, baseUrl, userInput, setUserInput }) => {
    const VARIANTES_URL = `${baseUrl}/api/variantes`;
    const [variantes, setVariantes] = useState([]);
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [varianteActiva, setVarianteActiva] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [expandido, setExpandido] = useState(false);

    const email = user?.primaryEmailAddress?.emailAddress;

    useEffect(() => {
        if (email && toolId) cargarVariantes();
    }, [email, toolId]);

    const cargarVariantes = async () => {
        try {
            setCargando(true);
            const res = await fetch(`${VARIANTES_URL}?email=${encodeURIComponent(email)}&toolId=${toolId}`);
            const data = await res.json();
            setVariantes(data.variantes || []);
        } catch (e) {
            console.error("Error cargando variantes:", e);
        } finally {
            setCargando(false);
        }
    };
    const guardarNueva = async () => {
        if (!nombre.trim()) {
            showNotification("Ingresá un nombre para la variante", "error");
            return;
        }
        try {
            setGuardando(true);
            const res = await fetch(VARIANTES_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    toolId,
                    nombre: nombre.trim(),
                    descripcion,
                    wizardData: {
                        ...wizardData, _userInput: userInput || '',
                        _userInput: document.querySelector('textarea[placeholder*="requerimiento"]')?.value || '',
                    }
                }),
            });
            const data = await res.json();
            if (data.success) {
                showNotification(`✅ Variante "${nombre}" guardada`, "success");
                setNombre('');
                setDescripcion('');
                setVarianteActiva(data.variante);
                await cargarVariantes();
            } else {
                showNotification(data.error || "Error al guardar", "error");
            }
        } catch (e) {
            showNotification("Error de conexión", "error");
        } finally {
            setGuardando(false);
        }
    };

    const actualizar = async () => {
        if (!varianteActiva) {
            showNotification("Seleccioná una variante para actualizar", "error");
            return;
        }
        try {
            setGuardando(true);
            const res = await fetch(`${VARIANTES_URL}/${varianteActiva.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    toolId,
                    nombre: nombre.trim(),
                    descripcion,
                    wizardData: {
                        ...wizardData, _userInput: userInput || '',
                        _userInput: document.querySelector('textarea[placeholder*="requerimiento"]')?.value || '',
                    }
                }),
            });
            const data = await res.json();
            if (data.success) {
                showNotification(`✅ Variante "${varianteActiva.nombre}" actualizada`, "success");
                await cargarVariantes();
            } else {
                showNotification(data.error || "Error al actualizar", "error");
            }
        } catch (e) {
            showNotification("Error de conexión", "error");
        } finally {
            setGuardando(false);
        }
    };

    const cargarVariante = (variante) => {
        setWizardData(variante.wizardData);
        setVarianteActiva(variante);
        setNombre(variante.nombre);
        setDescripcion(variante.descripcion || '');
        if (setUserInput && variante.wizardData?._userInput) {
            setUserInput(variante.wizardData._userInput);
        }
        showNotification(`📂 Variante "${variante.nombre}" cargada`, "info");
    };

    const eliminar = async (variante) => {
        if (!window.confirm(`¿Eliminar variante "${variante.nombre}"?`)) return;
        try {
            const res = await fetch(`${VARIANTES_URL}/${variante.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json();
            if (data.success) {
                showNotification(`🗑️ Variante eliminada`, "info");
                if (varianteActiva?.id === variante.id) {
                    setVarianteActiva(null);
                    setNombre('');
                    setDescripcion('');
                }
                await cargarVariantes();
            } else {
                showNotification(data.error || "Error al eliminar", "error");
            }
        } catch (e) {
            showNotification("Error de conexión", "error");
        }
    };

    const formatearFecha = (iso) => {
        return new Date(iso).toLocaleDateString('es-AR', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="bg-white/3 border border-white/8 rounded-[24px] overflow-hidden">

            {/* Header */}
            <button
                onClick={() => setExpandido(!expandido)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/5 transition-all"
            >
                <div className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-amber-500/15 border border-amber-500/30 rounded-xl flex items-center justify-center">
                        <FolderOpen size={13} className="text-amber-400" />
                    </div>
                    <div className="text-left">
                        <p className="text-[11px] font-black text-white uppercase tracking-wide">
                            Variantes
                            {varianteActiva && (
                                <span className="ml-2 text-amber-400">— {varianteActiva.nombre}</span>
                            )}
                        </p>
                        <p className="text-[9px] text-white/30">
                            {variantes.length} variante{variantes.length !== 1 ? 's' : ''} guardada{variantes.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                <span className="text-white/30 text-[10px]">{expandido ? '▲' : '▼'}</span>
            </button>

            {expandido && (
                <div className="border-t border-white/5 p-5 space-y-4">

                    {/* Lista de variantes */}
                    {cargando ? (
                        <div className="flex items-center justify-center py-6">
                            <span className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                        </div>
                    ) : variantes.length === 0 ? (
                        <p className="text-[10px] text-white/20 text-center py-4 italic">
                            No tenés variantes guardadas para esta herramienta
                        </p>
                    ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-hide">
                            {variantes.map((v) => (
                                <div
                                    key={v.id}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all cursor-pointer
                    ${varianteActiva?.id === v.id
                                            ? 'bg-amber-500/10 border-amber-500/40'
                                            : 'bg-white/3 border-white/8 hover:border-amber-500/30'
                                        }`}
                                    onClick={() => cargarVariante(v)}
                                >
                                    {varianteActiva?.id === v.id && (
                                        <Check size={12} className="text-amber-400 shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-black text-white truncate">{v.nombre}</p>
                                        {v.descripcion && (
                                            <p className="text-[9px] text-white/30 truncate">{v.descripcion}</p>
                                        )}
                                        <p className="text-[9px] text-white/20">{formatearFecha(v.updatedAt)}</p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); eliminar(v); }}
                                        className="p-1 text-white/20 hover:text-red-400 transition-colors shrink-0"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Formulario guardar */}
                    <div className="border-t border-white/5 pt-4 space-y-3">
                        <input
                            type="text"
                            placeholder="Nombre de la variante"
                            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-[11px] text-white placeholder:text-white/20 outline-none focus:border-amber-500/50"
                            value={nombre}
                            onChange={e => setNombre(e.target.value)}
                            maxLength={50}
                        />
                        <input
                            type="text"
                            placeholder="Descripción (opcional)"
                            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-[11px] text-white placeholder:text-white/20 outline-none focus:border-amber-500/50"
                            value={descripcion}
                            onChange={e => setDescripcion(e.target.value)}
                            maxLength={100}
                        />
                        <div className="flex gap-2">
                            <button
                                onClick={guardarNueva}
                                disabled={guardando || !nombre.trim()}
                                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all
                                ${guardando || !nombre.trim()
                                        ? 'bg-white/5 text-white/20 cursor-not-allowed'
                                        : 'bg-amber-500/20 border border-amber-500/40 text-amber-400 hover:bg-amber-500/30'
                                    }`}
                            >
                                {guardando ? (
                                    <span className="w-3 h-3 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                                ) : <Plus size={12} />}
                                Guardar Nueva
                            </button>
                            <button
                                onClick={actualizar}
                                disabled={guardando || !varianteActiva}
                                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all
                  ${guardando || !varianteActiva
                                        ? 'bg-white/5 text-white/20 cursor-not-allowed'
                                        : 'bg-blue-500/20 border border-blue-500/40 text-blue-400 hover:bg-blue-500/30'
                                    }`}
                            >
                                {guardando ? (
                                    <span className="w-3 h-3 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                                ) : <RefreshCw size={12} />}
                                Actualizar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VariantesPanel;